import React, { FC, HTMLAttributes } from 'react';
import styled from 'styled-components';

import { Button } from '~/atomic/atom/Button';
import { OpenBookIcon } from '~/atomic/atom/icon/OpenBookIcon';
import { ReaderLink } from '~/atomic/atom/links/dynamicLinks';
import { ListenAudioBookButton } from '~/atomic/page/book/Book/ListenAudioBookButton';
import {
  bookSelector,
  isBookPageEBookSelector,
} from '~/feature/book/book.selector';
import { isMobileSizeSelector } from '~/feature/media/windowSize.selector';
import { isHaveUserAbonnementSelector } from '~/feature/user/user.selector';
import { lessThan } from '~/lib/mediaQuery';
import { useAppSelector } from '~/store';

interface BookButtonsProps extends HTMLAttributes<HTMLDivElement> {
  isMobile?: Boolean;
}

export const BookPlayerButtons: FC<BookButtonsProps> = (props) => {
  const { bookPage } = useAppSelector(bookSelector);
  const isHaveUserAbonnement = useAppSelector(isHaveUserAbonnementSelector);
  const isHaveEbook = useAppSelector(isBookPageEBookSelector);
  const isMobileSize = useAppSelector(isMobileSizeSelector);

  const isFreeBook = bookPage?.free;

  if (isMobileSize) return null;

  const readBookTextRender = () => {
    if (isFreeBook || bookPage?.ebook?.bought) return 'Читать';

    if (isHaveUserAbonnement && bookPage?.ebook?.addedToAbonnement) return 'Читать по абонементу';

    return 'Читать';
  };

  return (
    <SCBookButtons {...props}>
      <div>
        {isHaveEbook ? (
          <SCReadBookWrapper>
            <ReaderLink bookSlug={bookPage?.slug}>
              <SCBookReadButtonLink>
                <SCBookReadButton size="large" type="primary">
                  <SCOpenBookIcon />
                  {readBookTextRender()}
                </SCBookReadButton>
              </SCBookReadButtonLink>
            </ReaderLink>
          </SCReadBookWrapper>
        ) : null}
      </div>
      <SCListenAudioBookButton
        book={{
          name: bookPage?.name,
          slug: bookPage?.slug,
          id: bookPage?.id,
          author: bookPage?.author,
          audiobook: bookPage?.audiobook,
        }}
        chapters={bookPage?.audiobook?.chapters?.map((chapter) => ({
          id: chapter.chapterId,
          name: chapter.name,
          url: chapter.audioUrl,
          duration: chapter.duration,
          available: chapter.available,
        }))}
        isShowText
        text="Слушать"
      />
      {/* <BookPlayerInMobileAppLink /> */}
    </SCBookButtons>
  );
};

const SCBookButtons = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  flex-shrink: 0;
  width: 100%;

  ${lessThan('lg')} {
    width: 100%;
    max-width: none;
    flex-direction: column;
  }
`;

const SCListenAudioBookButton = styled(ListenAudioBookButton)`
  max-width: 100%;
  background-color: var(--primary-color);

  width: 100%;
  animation-duration: 4s;
  animation-name: glossEffect;
  animation-delay: 5s;
  animation-iteration-count: infinite;

  &:hover {
    animation-play-state: paused;
  }
`;

export const SCBookReadButton = styled(Button)` && {
  @keyframes glossEffect {
    from {
      background-color: var(--primary-color);
    }
    
    50% {
      background-color: #6FACFE;
    }
    
    to {
      background-color: var(--primary-color);
    }
  }
  
  width: 100%;
  animation-duration: 4s;
  animation-name: glossEffect;
  animation-delay: 3s;
  animation-iteration-count: infinite;
  
  &:hover {
    animation-play-state: paused;
  }
}
`;

export const SCBookReadButtonLink = styled.a`
  width: 100%;
`;

const SCReadBookWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

const SCOpenBookIcon = styled(OpenBookIcon)`
  font-size: 18px;
  margin-right: 12px;
`;
